// B-100 — "Subir documentos" in an office's closing: the files the office sends by email (its GLORY
// count, DOCUMENTOS DEL CIERRE, TOTALES Y CUADRE), uploaded by a person when one did not arrive or
// came wrong, and the closing analysed again.
//
// The backend files them through the office email's own path (`uploadCierreOfficeFiles`): each file
// is the document its name says by the client's rules, anything else is kept beside the closing as an
// annex, the same bytes are not filed twice, and only this closing is read and evaluated. The screen
// shows the same decision before sending (`utils/cierreAnexosOficina.js`, over the rules
// `listCierreOfficeFileRules` answers — only the documents this office's profile reads, so an office
// without a GLORY is not asked for one): one row per document with the file that fills it, and the
// annexes apart. The files come the way the day's reports do (`ZonaArchivos`): chosen one by one, a
// folder, dragged, or a zip, opened here. A client whose offices send no documents of their own gets
// no button at all.
import { useEffect, useState } from "react";
import {
  AlertTriangle, CheckCircle2, CircleDashed, Equal, FileText, Paperclip, Upload, X,
} from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Spinner,
  cn,
} from "@mantis-sas/ui";

import { listCierreOfficeFileRules, uploadCierreOfficeFiles } from "../../../ApiCalls/mantisBusiness";
import { correctionError } from "../../../utils/cierreCorrections";
import { MOTIVOS_DE_EVIDENCIA } from "../../../utils/cierreDocuments";
import { nombreDeDocumentos, nombreDeFuente, parteDeFuente } from "../../../utils/cierreNombres";
import {
  MAX_ARCHIVOS_OFICINA, archivosAEnviar, clasificarArchivos, claveDeRegla, hojasDeLibro, sumarArchivos,
} from "../../../utils/cierreAnexosOficina";
import { expandirZips } from "../../../utils/cierreZip";
import { NombreDeArchivo } from "./NombreDeArchivo";
import { ZonaArchivos } from "./ZonaArchivos";
import { guia } from "../../../utils/cierreTours";
import { BotonGuia } from "./GuiaPantalla";

// `.ods`: Cotorra exports its GLORY as an OpenDocument sheet, which the office email already reads (B-134).
const ACCEPT = ".pdf,.xlsx,.xls,.ods,.csv,.zip,.jpg,.jpeg,.png";

const ESTADOS = {
  BALANCED: "cuadrado",
  RESOLVED: "resuelto",
  NEEDS_REVIEW: "por revisar",
  PARTIAL: "incompleto",
  SCHEDULED: "programado",
  RUNNING: "en proceso",
  FAILED: "con error",
};

// What became of each file, in the operator's words: one entry per file, with its own label and
// detail for the screen, and the whole sentence (`texto`).
export const lineasDeResultado = (resultado, nombres) => {
  if (!resultado) return [];
  const porArchivo = new Map();
  (resultado.stored || []).forEach(({ documentKey, filename }) => {
    porArchivo.set(filename, [...(porArchivo.get(filename) || []), documentKey]);
  });
  const leidos = [...porArchivo.entries()].map(([filename, claves]) => {
    const como = claves.length > 1
      ? `${nombreDeDocumentos(nombres, claves)} (${claves.map((c) => parteDeFuente(nombres, c)).join(", ")})`
      : nombreDeFuente(nombres, claves[0]);
    return {
      tono: "ok", archivo: filename, etiqueta: "Leído", detalle: `Como ${como}.`, texto: `${filename}: leído como ${como}.`,
    };
  });
  const anexos = (resultado.evidence || []).map(({ filename, reason, mensaje, accion }) => {
    // B-126 — the backend's sentence when it has one: why it was only kept, and what to do.
    const detalle = mensaje
      ? `${mensaje} ${accion || ""}`.trim()
      : `Guardado con el cierre, sin leerlo. ${MOTIVOS_DE_EVIDENCIA[reason] || ""}`.trim();
    return {
      tono: "neutral", archivo: filename, etiqueta: "Anexo", detalle, texto: `${filename}: ${detalle}`,
    };
  });
  const iguales = (resultado.skipped || []).map(({ filename }) => ({
    tono: "igual",
    archivo: filename,
    etiqueta: "Sin cambios",
    detalle: "Es el mismo archivo que ya tenía el cierre.",
    texto: `${filename}: es el mismo archivo que ya tenía el cierre; no cambió nada.`,
  }));
  const fallos = (resultado.failed || []).map(({ documentKey, error, mensaje, accion }) => {
    const quien = documentKey ? nombreDeFuente(nombres, documentKey) : "El cierre";
    // B-126 — what happened and what to do, never the engine's English when the backend said it.
    const detalle = mensaje ? `${mensaje} ${accion || ""}`.trim() : error;
    return {
      tono: "error", archivo: quien, etiqueta: "No se leyó", detalle, texto: `${quien}: ${detalle}`,
    };
  });
  return [...leidos, ...anexos, ...iguales, ...fallos];
};

const CHIP = {
  ok: "border-brinks-success-border bg-brinks-success-soft text-brinks-success",
  neutral: "border-brinks-border bg-brinks-surface text-brinks-muted-foreground",
  igual: "border-brinks-border bg-brinks-surface text-brinks-muted-foreground",
  error: "border-brinks-danger-border bg-brinks-danger-soft text-brinks-danger",
};
const ICONO = {
  ok: CheckCircle2, neutral: Paperclip, igual: Equal, error: AlertTriangle,
};
const COLOR_ICONO = {
  ok: "text-brinks-success", neutral: "text-brinks-muted-foreground", igual: "text-brinks-muted-foreground", error: "text-brinks-danger",
};

const Chip = ({ tono, children }) => (
  <span className={cn("inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold", CHIP[tono])}>
    {children}
  </span>
);

const Seccion = ({ titulo, extra, children, ayuda }) => (
  <section {...ayuda} className="overflow-hidden rounded-[10px] border bg-card">
    <header className="flex items-center justify-between gap-2 border-b bg-brinks-surface/60 px-3 py-2">
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.04em] text-brinks-muted-foreground">{titulo}</h3>
      {extra}
    </header>
    {children}
  </section>
);

export function SubirDocumentosOficina({
  token, mantisBusinessId, closingId, oficina, nombres, permitido = true, cerrado = false, onSubido,
}) {
  const [reglas, setReglas] = useState([]);
  const [abierto, setAbierto] = useState(false);
  const [archivos, setArchivos] = useState([]);
  const [abriendo, setAbriendo] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");
  const [avisos, setAvisos] = useState([]);
  const [resultado, setResultado] = useState(null);
  // B-132 — each workbook's sheet names, read once when it is added: the GLORY is told by them.
  const [hojas, setHojas] = useState(() => new Map());

  // Whether this closing's office sends documents of its own decides the button.
  useEffect(() => {
    let activo = true;
    setReglas([]);
    if (!closingId) return undefined;
    Promise.resolve()
      .then(() => listCierreOfficeFileRules(token, mantisBusinessId, closingId))
      .then((respuesta) => {
        if (activo && respuesta?.success) setReglas(respuesta.data?.rules || []);
      })
      .catch(() => {});
    return () => {
      activo = false;
    };
  }, [token, mantisBusinessId, closingId]);

  if (!reglas.length || !permitido) return null;

  const clasificacion = clasificarArchivos(archivos, reglas, (archivo) => hojas.get(archivo) || null);
  const aEnviar = archivosAEnviar(clasificacion, reglas);
  const demasiados = aEnviar.length > MAX_ARCHIVOS_OFICINA;

  const abrir = () => {
    setArchivos([]);
    setError("");
    setAvisos([]);
    setResultado(null);
    setAbierto(true);
  };

  // A zip is opened here, so its files show on their rows before anything is sent.
  const agregar = async (lista) => {
    setResultado(null);
    setError("");
    setAbriendo(true);
    const { archivos: abiertos, errores } = await expandirZips(lista).catch(() => ({ archivos: [], errores: ["No se pudieron abrir los archivos."] }));
    const leidas = reglas.some((regla) => regla.sheets)
      ? await Promise.all(abiertos.map(async (archivo) => [archivo, await hojasDeLibro(archivo)]))
      : [];
    setHojas((actuales) => new Map([...actuales, ...leidas.filter(([, nombres]) => nombres)]));
    setAbriendo(false);
    setAvisos(errores);
    setArchivos((actuales) => sumarArchivos(actuales, abiertos));
  };

  const quitar = (archivo) => setArchivos((actuales) => actuales.filter((a) => a !== archivo));

  const subir = async () => {
    setEnviando(true);
    setError("");
    const respuesta = await uploadCierreOfficeFiles(token, mantisBusinessId, closingId, aEnviar).catch(() => null);
    setEnviando(false);
    if (!respuesta?.success) {
      setError(correctionError(respuesta));
      return;
    }
    setResultado(respuesta.data);
    setArchivos([]);
    if (onSubido) onSubido();
  };

  const lineas = lineasDeResultado(resultado, nombres);
  const ocupado = enviando || abriendo;

  return (
    <>
      <button
        type="button"
        {...guia("Subir documentos", "Sube los archivos de la oficina que no llegaron por correo o llegaron mal; el cierre se vuelve a analizar.")}
        onClick={abrir}
        disabled={cerrado}
        title={cerrado ? "El cierre está cerrado: reábrelo para subirle documentos." : "Sube los archivos de la oficina y vuelve a analizar el cierre."}
        className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-[8px] border border-brinks-border px-2.5 text-[12.5px] font-medium text-brinks-foreground hover:bg-brinks-surface-hover disabled:opacity-60"
      >
        <Upload className="h-3.5 w-3.5" /> Subir documentos
      </button>
      <Dialog open={abierto} onOpenChange={(abrirlo) => !ocupado && setAbierto(abrirlo)}>
        <DialogContent className="brinks-scope font-redhat flex max-h-[90vh] max-w-xl flex-col gap-0 overflow-y-auto p-0">
          <DialogHeader className="border-b p-4 text-left">
            <div className="flex min-w-0 items-center gap-1">
              <DialogTitle className="text-[16px] font-semibold text-brinks-foreground">Subir documentos{oficina ? ` de ${oficina}` : ""}</DialogTitle>
              <BotonGuia guiaId="dialogo-subir-documentos" titulo="Subir documentos" texto="Sube los archivos que la oficina manda por correo cuando no llegaron o llegaron mal. Antes de enviarlos ves a qué documento va cada uno." />
            </div>
            <DialogDescription className="text-[12.5px] text-brinks-muted-foreground">
              Los mismos archivos que la oficina manda por correo. Cada uno se reconoce por su nombre o por su contenido, y el cierre se vuelve a analizar.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 p-4 text-[12.5px]">
            {!resultado ? (
              <>
                <ZonaArchivos
                  id="subir-documentos-oficina"
                  titulo="Elegir archivos, una carpeta o un zip"
                  ayuda="Arrastra aquí los archivos o la carpeta de la oficina. Un zip se abre aquí mismo."
                  etiqueta="Archivos de la oficina"
                  accept={ACCEPT}
                  deshabilitada={ocupado}
                  onArchivos={agregar}
                />
                {abriendo ? (
                  <p role="status" className="flex items-center gap-2 text-brinks-muted-foreground">
                    <Spinner className="h-4 w-4" /> Abriendo los archivos…
                  </p>
                ) : null}
                {avisos.map((aviso) => (
                  <p key={aviso} role="alert" className="rounded-[8px] border border-brinks-warning-border bg-brinks-warning-soft px-2.5 py-1.5 text-brinks-warning">{aviso}</p>
                ))}

                <Seccion
                  ayuda={guia("Lo que manda esta oficina", "Cada documento que lee esta oficina. Al agregar archivos, cada uno aparece en el documento que llena; \"Sin archivo\" es lo que todavía falta.")}
                  titulo="Lo que manda esta oficina"
                  extra={(
                    <span className="text-[11.5px] tabular-nums text-brinks-muted-foreground">
                      {Object.keys(clasificacion.porRegla).length} de {reglas.length}
                    </span>
                  )}
                >
                  <ul className="divide-y">
                    {reglas.map((regla) => {
                      const archivo = clasificacion.porRegla[claveDeRegla(regla)];
                      return (
                        <li key={claveDeRegla(regla)} className="flex items-center gap-2.5 px-3 py-2">
                          <span className={cn(
                            "grid h-7 w-7 shrink-0 place-items-center rounded-[7px]",
                            archivo ? "bg-brinks-success-soft text-brinks-success" : "bg-brinks-surface text-brinks-muted-foreground"
                          )}
                          >
                            {archivo ? <CheckCircle2 className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
                          </span>
                          <div className="min-w-0 flex-1">
                            <NombreDeArchivo nombre={nombreDeDocumentos(nombres, regla.documents)} />
                            {archivo ? (
                              <span className="block truncate text-[11.5px] text-brinks-muted-foreground" title={archivo.name}>{archivo.name}</span>
                            ) : null}
                          </div>
                          {archivo ? (
                            <button
                              type="button"
                              aria-label={`Quitar ${archivo.name}`}
                              disabled={ocupado}
                              onClick={() => quitar(archivo)}
                              className="shrink-0 text-brinks-muted-foreground hover:text-brinks-foreground"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          ) : (
                            <span className="inline-flex shrink-0 items-center gap-1 text-[11.5px] text-brinks-muted-foreground">
                              <CircleDashed className="h-3.5 w-3.5" /> Sin archivo
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </Seccion>

                <Seccion
                  ayuda={guia("Anexos", "Los archivos que no llenan ningún documento: se guardan en el cierre como soporte.")}
                  titulo="Anexos"
                  extra={<span className="text-[11.5px] tabular-nums text-brinks-muted-foreground">{clasificacion.anexos.length}</span>}
                >
                  {clasificacion.anexos.length ? (
                    <ul className="flex flex-wrap gap-1.5 p-2.5">
                      {clasificacion.anexos.map((archivo) => (
                        <li key={archivo.name} className="inline-flex max-w-full items-center gap-1.5 rounded-full border bg-brinks-surface py-0.5 pl-2.5 pr-1.5">
                          <Paperclip className="h-3 w-3 shrink-0 text-brinks-muted-foreground" />
                          <span className="truncate text-[12px] text-brinks-foreground" title={archivo.name}>{archivo.name}</span>
                          <button
                            type="button"
                            aria-label={`Quitar ${archivo.name}`}
                            disabled={ocupado}
                            onClick={() => quitar(archivo)}
                            className="shrink-0 text-brinks-muted-foreground hover:text-brinks-foreground"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="px-3 py-2 text-brinks-muted-foreground">
                      Los demás archivos de la oficina (CARTERA, CONVENIOS…) se guardan con el cierre, sin leerse.
                    </p>
                  )}
                </Seccion>

                {clasificacion.repetidos.length ? (
                  <p className="rounded-[8px] border border-brinks-warning-border bg-brinks-warning-soft px-2.5 py-1.5 text-brinks-warning">
                    No se envían, porque ya hay uno más reciente para el mismo documento:
                    {" "}
                    {clasificacion.repetidos.map((a) => a.name).join(", ")}.
                  </p>
                ) : null}
                {demasiados ? (
                  <p role="alert" className="rounded-[8px] border border-brinks-danger-border bg-brinks-danger-soft px-2.5 py-1.5 text-brinks-danger">
                    Son {aEnviar.length} archivos y se pueden subir {MAX_ARCHIVOS_OFICINA} a la vez: quita algunos anexos.
                  </p>
                ) : null}
              </>
            ) : null}

            {enviando ? (
              <p role="status" className="flex items-center gap-2 text-brinks-muted-foreground">
                <Spinner className="h-4 w-4" /> Subiendo y leyendo los archivos; puede tardar un minuto.
              </p>
            ) : null}
            {error ? (
              <p role="alert" className="rounded-[8px] border border-brinks-danger-border bg-brinks-danger-soft px-2.5 py-1.5 text-brinks-danger">{error}</p>
            ) : null}

            {resultado ? (
              <div role="status" className="space-y-3">
                <Seccion
                  ayuda={guia("Qué pasó con cada archivo", "El resultado de cada archivo: leído, guardado como anexo, sin cambios o no leído, y el nuevo estado del cierre.")}
                  titulo="Qué pasó con cada archivo"
                  extra={resultado.state ? (
                    <Chip tono={resultado.state === "BALANCED" || resultado.state === "RESOLVED" ? "ok" : "neutral"}>
                      Cierre {ESTADOS[resultado.state] || resultado.state}
                    </Chip>
                  ) : null}
                >
                  <ul className="divide-y">
                    {lineas.map((linea) => {
                      const Icono = ICONO[linea.tono];
                      return (
                        <li key={linea.texto} className="flex items-start gap-2.5 px-3 py-2" title={linea.texto}>
                          <Icono className={cn("mt-0.5 h-4 w-4 shrink-0", COLOR_ICONO[linea.tono])} />
                          <div className="min-w-0 flex-1">
                            <span className="block truncate font-medium text-brinks-foreground">{linea.archivo}</span>
                            <span className="block text-[11.5px] text-brinks-muted-foreground">{linea.detalle}</span>
                          </div>
                          <Chip tono={linea.tono}>{linea.etiqueta}</Chip>
                        </li>
                      );
                    })}
                  </ul>
                </Seccion>
                {resultado.state ? (
                  <p className="font-medium text-brinks-foreground">Cierre analizado de nuevo: quedó {ESTADOS[resultado.state] || resultado.state}.</p>
                ) : null}
              </div>
            ) : null}
          </div>

          <DialogFooter className="sticky bottom-0 flex-row justify-end gap-2 border-t bg-inherit p-3 sm:space-x-0">
            <Button variant="outline" className="h-9" disabled={ocupado} onClick={() => setAbierto(false)}>
              {resultado ? "Listo" : "Cancelar"}
            </Button>
            {resultado ? (
              <Button variant="outline" className="h-9 gap-1.5" onClick={() => setResultado(null)}>
                <Upload className="h-4 w-4" /> Subir más
              </Button>
            ) : (
              <Button {...guia("Subir y volver a analizar", "Envía los archivos; el cierre se vuelve a analizar con ellos.")} className="h-9 gap-1.5" disabled={!aEnviar.length || ocupado || demasiados} onClick={subir}>
                {enviando ? <Spinner className="h-4 w-4" /> : <Upload className="h-4 w-4" />}
                Subir y volver a analizar
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default SubirDocumentosOficina;
